/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:31:49                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-26 14:46:56                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Services
  * Components
  * TechnicianSelect: technician select component
    * useEffect: Manage isMulti parameter and display
    * getTechnicians: Get technicians list
    * technicianChange: handle selector change 
*/

/* Imports */
import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";
import Select from "react-select";

/***/

/* Services */
import { api } from "../../../services/api/api";
import { setSelectList } from "../../../services/utils/utils";
/***/

/* Components */
import TechnicianOptionSelectDetails from "./TechnicianOptionSelectDetails";
import MultiCheckSelect from "../../option-select/MultiCheckSelect";
import { reactSelectTheme } from "../../option-select/reactSelectTheme";
import Switch from "../../switch/Switch";
import { format } from "date-fns";
/***/

/**
* TechnicianSelect: technician select component
* Used to select a technician entity, it can open the 
* technician edit component to create or edit a technician.
* @param value - current value
* @param onChange - on value change callback
* @param disabled - field disabled state
* @param clearable - allow field clear feature
* @param isMulti - allow multi selection
* @param className - additional classNames
*/
export default function TechnicianSelect({
  value,
  onChange,
  disabled,
  clearable,
  isMulti,
  address,
  schedule,
  isDetailed,
  className = "",
  onToggleShowUnavailable,
  onUnavailablesChange
}: {
  value: Partial<EasyTech.Technician> | Partial<EasyTech.Technician>[] | undefined,
  onChange?: (value: any) => void,
  disabled?: boolean
  clearable?: boolean,
  isMulti?: boolean,
  address?: EasyTech.Address,
  schedule?: EasyTech.WorkorderSchelude,
  isDetailed?: boolean,
  className?: string,
  onToggleShowUnavailable?: (show: boolean) => void,
  onUnavailablesChange?: (techs: EasyTech.Technician[]) => void
}) {
  const { t } = useTranslation();
  const [valueState, setValueState] = useState<any>(null);
  const [timestamp, setTimestamp] = useState<number | null>(null);
  const [showUnavail, setShowUnavail] = useState<boolean>(false);
  const [unavails, setUnavails] = useState<EasyTech.Technician[]>();

  /*
  * getTechnicians: Get technicians list
  * @return - technicians list
  */
  const getTechnicians = (inputValue: string): Promise<EasyTech.IFormData<EasyTech.Technician>[]> => {
    return new Promise((resolve) => {
      if (disabled || timestamp === null) return resolve([]);

      const searchParams = new URLSearchParams();
      if (inputValue) {
        searchParams.append("name", inputValue);
      }

      api.get(`admin/technicians/search?${searchParams.toString()}`).then(async (ret) => {
        const val: any = structuredClone(value);
        let tmp = (ret as EasyTech.Technician[]);

        let unavail: EasyTech.Technician[];
        if (address) {
          unavail = await getUnavail();
          const itinerarys: EasyTech.TechnicianItinerary[] = await getNearest();
          tmp.forEach((el) => {
            const it = itinerarys.find((it) => it.technicianId === el.id);
            if (it) el.itinerary = it;
          });
          tmp.sort((a, b) => {
            if (a.itinerary && b.itinerary)
              return a.itinerary.travelTimeInSeconds - b.itinerary.travelTimeInSeconds;
            if (a.itinerary && !b.itinerary)
              return -1;
            return 0;
          });
        }

        const unavails: EasyTech.Technician[] = [];
        tmp = tmp.filter((el) => {
          if (unavail) {
            const un = unavail.find((un) => un.id === el.id);
            if (un) {
              unavails.push(el);
              return false;
            } else {
              return true;
            }
          }
          if (value && !isMulti && val.id && el.id === val.id) {
            return false;
          }

          return true;
        });
        if (address) {
          setUnavails(unavails);
          if (onUnavailablesChange) onUnavailablesChange(unavails);
        }
        return resolve(
          setSelectList<EasyTech.Technician>(tmp, (el) => (`${el.firstName} ${el.lastName}`))
        );
      }).catch((err) => {
        console.error(err);
        return resolve([]);
      });
    });
  };
  /***/

  /**
  * getTechnicians: Get technicians list
  * @return - technicians list
  */
  const getNearest = (): Promise<EasyTech.TechnicianItinerary[]> => {
    return new Promise(resolve => {
      if (!address) return;
      const searchParams = new URLSearchParams();
      searchParams.append("lat", address.latitude.toString());
      searchParams.append("lng", address.longitude.toString());

      api.get(`admin/technician_locations/nearest?${searchParams.toString()}`).then((ret) => {
        return resolve((ret as EasyTech.TechnicianItinerary[]));
      }).catch((err) => {
        console.error(err);
        return resolve([]);
      });
    });
  };
  /***/

  /**
  * getTechnicians: Get technicians list
  * @return - technicians list
  */
  const getUnavail = (): Promise<EasyTech.Technician[]> => {
    return new Promise(resolve => {
      if (schedule) {
        const fromDate = format(new Date(schedule.start), "yyyy-MM-dd'T'HH:mm:ss");
        const toDate = format(new Date(schedule.end), "yyyy-MM-dd'T'HH:mm:ss");
        const url = `admin/technician_times/unavailability?from=${fromDate}&to=${toDate}`;
        api.get(url).then((ret) => {
          return resolve((ret as EasyTech.Technician[]));
        }).catch((err) => {
          console.error(err);
          return resolve([]);
        });
      } else {
        resolve([]);
      }
    });
  };
  /***/

  /**
  * technicianChange: handle selector change 
  * @param value - new value
  */
  const technicianChange = ((value: any) => {
    const ret = isMulti ? [] : (value ? value.value : value);

    if (isMulti) {
      for (let i = 0; i < value.length; i++) {
        ret.push(value[i] ? value[i].value : value[i]);
      }
    }

    setValueState(value);
    if (onChange) onChange(ret);
  });
  /***/

  const toggleUnavail = () => {
    const newVal = !showUnavail;
    setShowUnavail(newVal);
    if (onToggleShowUnavailable) onToggleShowUnavailable(newVal);
  };

  /**
  * useEffect: Manage isMulti parameter and display
  * @param value - selector current value
  * @param isMulti - selector isMulti param
  */
  useEffect(() => {
    if (value && !isMulti) {
      const tmp = value as EasyTech.Technician;
      setValueState({ value: tmp, label: `${tmp.firstName} ${tmp.lastName}` });
    }

    if (value && isMulti) {
      const vals = [];
      const tmp = value as EasyTech.Technician[];

      for (let i = 0; i < tmp.length; i++) {
        vals.push({ value: tmp[i], label: `${tmp[i].firstName} ${tmp[i].lastName}` });
      }
      setValueState(vals);
    }

    if (!value) setValueState(null);
  }, [value, isMulti]);
  /***/

  /**
  * useEffect: Manage isMulti parameter and display
  * @param value - selector current value
  * @param isMulti - selector isMulti param
  */
  useEffect(() => {
    setTimestamp(Date.now());
  }, [schedule, address]);
  /***/
  const SelectField = isMulti ? MultiCheckSelect : AsyncSelect;
  const UnavailField = isMulti ? MultiCheckSelect : Select;

  return (
    <>
      <div className={`flex w-full min-w-0 ${className}`}>
        <SelectField cacheOptions
          key={timestamp}
          defaultOptions
          loadOptions={getTechnicians}
          value={showUnavail ? undefined : valueState}
          theme={reactSelectTheme}
          onChange={technicianChange}
          name="technician"
          placeholder={t("common:selectTechnician")}
          className="min-w-0 w-full"
          classNamePrefix="react-select"
          isClearable={clearable}
          isDisabled={disabled || showUnavail}
          isMulti={isMulti}
          closeMenuOnSelect={!isMulti}
          hideSelectedOptions={!isMulti}
          components={
            isMulti ?
              undefined
              :
              { Option: (props: any) => <TechnicianOptionSelectDetails {...props} detailed={isDetailed} /> }
          } />
      </div>

      {address && !disabled &&
        <>
          <div className="flex my-4 gap-5">
            <div className="flex flex-col">
              <label>{t("common:lookForUnavailableTechnician")}</label>

              <span className="text-sm text-gray-500 mt-1">
                {t("common:lookForUnavailableTechnicianDescription")}
              </span>
            </div>
            <Switch checked={showUnavail}
              disabled={disabled}
              onChange={toggleUnavail} />
          </div>
          {showUnavail &&
            <UnavailField options={unavails ? setSelectList<EasyTech.Technician>(unavails, (el) => (`${el.firstName} ${el.lastName}`)) : []}
              value={valueState}
              theme={reactSelectTheme}
              onChange={technicianChange}
              className={`min-w-0 w-full ${className}`}
              classNamePrefix="react-select"
              placeholder={t("common:select")}
              isMulti={isMulti}
              closeMenuOnSelect={!isMulti}
              hideSelectedOptions={!isMulti}
              components={
                isMulti ?
                  undefined
                  :
                  { Option: (props: any) => <TechnicianOptionSelectDetails {...props} detailed={isDetailed} /> }
              }
            />
          }
        </>
      }
    </>
  );
}
/***/
